import { host, bch_host } from "../config";
import { dateToString } from "../components/utilities";
import { link } from "../config";
import { setSession } from "../store/reducer";

let apikey = process.env.REACT_APP_API_KEY;
apikey = "a0c5ef39b8472de0f9f050ebe8e20c3e";

export async function getCountries() {
  /**
   * Retrieves the list of countries.
   * @returns {Promise<Object[]>} The list of countries.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  const response = await fetch(link + "getCountries", {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });
  const data = await response.json();
  return data;
}

export async function getWeddingDateStatus(dateFrom, dateTo, hotelID) {
  /**
   * Retrieves the wedding date status for a given hotel and date range.
   * @param {Date} dateFrom The start date of the range.
   * @param {Date} dateTo The end date of the range.
   * @param {number} hotelID The ID of the hotel.
   * @returns {Promise<Object[]>} The object containing the wedding date status.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  const response = await fetch(
    link +
      `getWeddingCalendar&hotelID=${hotelID}&dateFrom=${dateToString(
        dateFrom
      )}&dateTo=${dateToString(dateTo)}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
      },
    }
  );
  const data = await response.json();
  if (data.status === 200) {
    return data.result;
  }
}

export async function getUserTypes(userGroupID = 0) {
  /**
   * Retrieves a list of user types from the server.
   *
   * @return {Object} An object containing the user types if the request is successful.
   */

  const response = await fetch(link + `getUserGroup&id=${userGroupID}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });
  const data = await response.json();

  return data;
}

export async function getHotels() {
  /**
   * Retrieves a list of hotels from the server.
   *
   * @return {Object[]} An array of hotel objects if the request is successful.
   */
  const response = await fetch(link + "getHotels", {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });
  const data = await response.json();
  if (data.status === 200) {
    return data.result;
  }
}

export async function getMarkets() {
  /**
   * The function `getMarkets` makes a GET request to fetch market data from a specified endpoint and
   * returns the data as a JSON object.
   * @returns The `getMarkets` function is returning the data fetched from the "/getMarkets" endpoint
   * after converting the response to JSON format.
   */
  const response = await fetch(link + "getMarkets", {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });
  const data = await response.json();
  return data;
}

export async function getUser(userID) {
  /**
   * Retrieves a user by their ID.
   * @param {string} userID - The user ID.
   * @returns {Promise<Object>} - The user object if the request is successful, else null.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  let url = link + `getUser&id=${userID}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "*/*",
    },
  });
  const data = await response.json();
  if (data.status === 200) {
    return data.result;
  } else {
    return null;
  }
}

export async function getUsers(userID) {
  /**
   * Retrieves the list of users created by the given user ID.
   * @param {string} userID - The user ID.
   * @returns {Promise<Object[]>} - The list of users created by the given user ID.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  let url = link + `getUsersCreatedByID&id=${userID}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "*/*",
    },
  });
  const data = await response.json();
  if (data.status === 200) {
    return data.result;
  }
  return null;
}

export async function getRequestedUsers(userID) {
  /**
   * Retrieves the list of requested users by the given manager user ID.
   * @param {string} userID - The manager user ID.
   * @returns {Promise<Object[]>} - The list of requested users.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  let url = link + `getRequestedUsersByManagerID&id=${userID}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "*/*",
    },
  });
  const data = await response.json();
  if (data.status === 200) {
    return data.result;
  }
  return null;
}

export async function getLoginBg() {
  /**
   * Fetches the login background image from the API.
   *
   * @returns {Promise<Object|null>} The object containing the login background image, or null if the request fails.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  let url = `${host}/api/beachcomber/B2B_login_data`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
      },
    });
    if (!response) {
      console.log("error:", response);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error("Fetch Error:", error);
    throw error;
  }
}

export async function getGalerieImages(lang) {
  /**
   * Retrieves the welcome page images from the API.
   *
   * @param {string} lang The language code for the API request.
   * @returns {Promise<Object|null>} The object containing the welcome page title and images, or null if the request fails.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  let url = `${host}/${lang}/api/beachcomber/node/welcome_page`;

  try {
    await refreshSession();
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
        "api-key": apikey,
      },
    });

    if (!response) {
      console.log("error:", response);
      return null;
    }

    let resp = await response.json();

    if (resp && resp.data && resp.data.length > 0) {
      let galerie = [];
      let wlc_title = "";
      resp.data.forEach((item) => {
        let result = item;

        wlc_title = result.attributes?.title || "";

        let list = result.relationships?.list || [];

        list.forEach((element) => {
          
          let temp = {
            desc:
              element.field_description_desktop?.replace(
                /<\/?[^>]+(>|$)/g,
                ""
              ) || "",
            itemImageSrc:
              element.field_image?.[0]?.imageDerivatives?.default_url || "",
            itemImageMobile:
              element.field_image?.[0]?.imageDerivatives?.silder_mobile?.url ||
              "",
            itemVideo:element.field_media_video_file?  element.field_media_video_file[0].default_url: "",
          
            alt: element.field_image?.[0]?.alt || "",
            thumbnailImageSrc: element.field_image?.[0]?.alt || "",
          };
          if (temp.itemImageSrc !== "") {
            galerie.push(temp);
          }
          if (temp.itemVideo !== "") {
            galerie.push(temp);
          }
        });
      });

      return { title: wlc_title, data: galerie };
    }
  } catch (error) {
    throw error;
  }
}

export async function getLastestDoc(lang) {
  /**
   * Retrieves the latest documents.
   * @param {string} lang - language code
   * @returns {Promise<Object[]>} - an array of objects containing the document
   * title and image url
   */

  let url = `${host}/${lang}/api/beachcomber/views/b2b_document/lists?page=0&items_per_page=10&api-key=a0c5ef39b8472de0f9f050ebe8e20c3e`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
        "api-key": apikey,
      },
    });

    if (!response) {
      console.log("error:", response);
      return null;
    }

    let data = await response.json();

    const formData = JSON.stringify({ data: data });

    const res = await fetch(link + `saveLatestDocuments`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        Accept: "application/json, text/plain, */*",
      },
      body: formData,
    });

    if (!res) {
      console.log("error:", res);
      return null;
    }

    return await res.json();
    return [];
  } catch (error) {
    throw error;
  }
}

export async function getHotelQuickInfo(lang, hotelID) {
  /**
   * Fetches hotel quick info from the API.
   *
   * @param {string} lang The language code for the API request.
   * @param {number} hotelID The ID of the hotel to fetch.
   * @returns {Promise<object|null>} The object containing the hotel's quick info, or null if the request fails.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  let url = `${host}/${lang}/api/beachcomber/hotel/${hotelID}`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
        "api-key": apikey,
      },
    });
    if (!response) {
      console.log("error:", response);
      return null;
    }
    await refreshSession();
    return await response.json();
  } catch (error) {
    throw error;
  }
}

export async function getDocumentCategories(lang) {
  /**
   * Fetches the list of document categories from the API.
   *
   * @param {string} [lang=en] The language code for the API request.
   * @returns {Promise<object[]>} The list of document categories.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  let url = `${host}/${lang || "en"}/api/beachcomber/documents/categories`;
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
        "api-key": apikey,
      },
    });
    if (!response) {
      console.log("error:", response);
      return null;
    }
    await refreshSession();
    const data = await response.json();

    return data;
  } catch (error) {
    throw error;
  }
}

export async function getSeasonality(lang) {
  /**
   * Fetches the list of document seasons from the API.
   *
   * @param {string} [lang=en] The language code for the API request.
   * @returns {Promise<object[]>} The list of document seasons.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  let url = `${host}/${lang || "en"}/api/beachcomber/documents/seasons`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
        "api-key": apikey,
      },
    });

    if (!response) {
      console.log("error:", response);
      return null;
    }
    const data = await response.json();

    return data;
  } catch (error) {
    throw error;
  }
}

export async function getDocByCatIDAndHotelID(lang, CatID, HotelID, season) {
  /**
   * Fetches the list of documents from the API by category ID and hotel ID.
   *
   * @param {string} lang The language code for the API request.
   * @param {number} CatID The category ID.
   * @param {number} [HotelID=null] The hotel ID.
   * @param {number} [season=null] The season ID.
   * @returns {Promise<object[]>} The list of documents.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  let params = {
    category_id: CatID,
  };

  if (HotelID) {
    if (HotelID === "15") {
      params.type_document = "Groupe";
    } else {
      params.hotel_id = HotelID;
    }
  }

  if (season) {
    params.season_id = season;
  }

  let url = new URL(`${host}/${lang}/api/beachcomber/views/b2b_document/lists`);
  url.search = new URLSearchParams(params).toString();

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "*/*",
        "api-key": apikey,
      },
    });

    if (!response) {
      console.log("error:", response);
      return null;
    }
    await refreshSession();
    const resp = await response.json();

    if ("data" in resp) {
      return resp["data"].length > 0 ? resp["data"] : [];
    } else {
      return [];
    }
  } catch (error) {
    console.log("error:", error);
    return null;
  }
}

export async function getHotelRoomStatus(hotelID, dateFrom, dateTo, roomID) {
  /**
   * Retrieves the room status for a given hotel and date range.
   *
   * @param {number} hotelID The ID of the hotel.
   * @param {Date} dateFrom The start date of the range.
   * @param {Date} dateTo The end date of the range.
   * @param {number} roomID The ID of the room.
   * @returns {Promise<object[]>} The array of room status objects.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  // console.log('link:',link +
  //   `/getHotelRoomStatus?hotelID=${hotelID}&dateFrom=${dateToString(
  //     dateFrom
  //   )}&dateTo=${dateToString(dateTo)}&room_id=${roomID}`);
  
  const response = await fetch(
    link +
      `getHotelRoomStatus&hotelID=${hotelID}&dateFrom=${dateToString(
        dateFrom
      )}&dateTo=${dateToString(dateTo)}&room_id=${roomID}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
      },
    }
  );

  const data = await response.json();

  if (data.status === 200) {
    return data.result;
  }
}

export async function getHotelRooms(hotelID) {
  /**
   * Retrieves the list of rooms for a given hotel.
   * @param {number} hotelID The ID of the hotel.
   * @returns {Promise<object[]>} The array of room objects.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  const response = await fetch(link + `getHotelRooms&hotelID=${hotelID}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });
  const data = await response.json();

  if (data.status === 200) {
    return data.result;
  }
}

export async function saveNewDocuments() {
  /**
   * Saves the latest documents for all hotels.
   *
   * @returns {Promise<boolean>} true if the documents were saved successfully, false otherwise.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  const response = await fetch(link + `SendLatestDocs`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });

  const data = await response.json();

  if (data.status === 200) {
    return data.result;
  }
}

export async function getNewDocuments(userID) {
  /**
   * Retrieves the latest documents for a given user ID.
   * @param {string} userID - The user ID.
   * @returns {Promise<Object[]>} - The array of objects containing the document title, image url and id.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  const response = await fetch(link + `getLatestDocuments&userID=${userID}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });
  const data = await response.json();

  if (data.status === 200) {
    return data.result;
  }
}

export async function saveDocumentsViewByUser(values) {
  /**
   * Saves the viewed documents for a given user ID.
   * @param {Object} values - The object containing the user ID and the viewed document IDs.
   * @returns {Promise<Object>} - The object containing the result of the save operation.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  const formData = JSON.stringify({ formData: values });
  const response = await fetch(link + `saveLatestDocumentViewByUser`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      Accept: "application/json, text/plain, */*",
    },
    body: formData,
  });

  const data = await response.json();

  if (data.status === 200) {
    return data.result;
  }
}

export async function getRoomStatusFiles(year, month) {
  /**
   * Retrieves the room status files for a given year and month.
   * @param {number} year The year for which the files are requested.
   * @param {number} month The month for which the files are requested.
   * @returns {Promise<Object[]>} The array of objects containing the file name, file size in bytes and the date uploaded, sorted by date uploaded in descending order.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  const response = await fetch(
    link + `getRoomStatusFiles&year=${year}&month=${month}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
      },
    }
  );
  const data = await response.json();

  if (data.status === 200) {
    let obj = data.result;
    let temp = [];

    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        temp = temp.concat(obj[key]);
      }
    }
    let files = temp.sort(
      (a, b) => new Date(b.dateUploaded) - new Date(a.dateUploaded)
    );

    return files.reverse();
  }
}

export async function sendCart(values) {
  const formData = JSON.stringify({ formData: values });
  const response = await fetch(link + `sendCart`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
    body: formData,
  });

  const data = await response.json();

  if (data.status === 200) {
    return data.result;
  }
}

export async function getDestination(lang) {
  /**
   * Retrieves the destination data from the API.
   * @param {string} lang The language code for the API request.
   * @returns {Promise<Object[]>} The array of objects containing the destination data.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  let url = `${host}/api/beachcomber/node/b2b_destination?api-key=${apikey}`;

  try {
    const response = await fetch(url, {
      method: "GET",
    });

    if (!response) {
      console.log("error:", response);
      return null;
    }
    const data = await response.json();
    await refreshSession();
    return data.data;
  } catch (error) {
    throw error;
  }
}

export async function saveCart(values) {
  /**
   * Saves the cart data on the server.
   * @param {Object} values - The object containing the cart data to be saved.
   * @returns {Promise<Object>} - The object containing the result of the save operation.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  const formData = JSON.stringify({ formData: values });

  let response = await fetch(link + "saveCart", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      Accept: "application/json, text/plain, */*",
    },
    body: formData,
  });

  const data = await response.json();
  if (data.status === 200) {
    return data.result;
  }
}

export async function deleteCart(values) {
  /**
   * Deletes the cart data on the server.
   * @param {Object} values - The object containing the cart data to be deleted.
   * @returns {Promise<Object>} - The object containing the result of the delete operation.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  const formData = JSON.stringify({ formData: values });

  let response = await fetch(link + "deleteCart", {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      Accept: "application/json, text/plain, */*",
    },
    body: formData,
  });
  const data = await response.json();
  if (data.status === 200) {
    return data.result;
  }
}

export async function getCart(userID) {
  /**
   * Retrieves the cart data from the server.
   * @param {string} userID The user ID for which the cart data should be retrieved.
   * @returns {Promise<Object[]>} The array of objects containing the cart data.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  let url = link + `getCart&id=${userID}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "*/*",
    },
  });
  const data = await response.json();

  if (data.status === 200) {
    const updatedResult = data.result.map((item) => {
      let temp;
      temp =
        typeof item["file"] === "string"
          ? JSON.parse(item["file"])
          : item["file"];
      temp["id"] = item["id"];
      return temp;
    });

    return updatedResult;
  } else {
    return null;
  }
}

export async function getSharedCartFiles(userID, email) {
  /**
   * Retrieves the list of files shared by a user to another user.
   *
   * @param {string} userID - The ID of the user who shared the files.
   * @param {string} email - The email address of the recipient.
   * @returns {Promise<Object[]>} - The list of shared files.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  const response = await fetch(
    link + `getSharedCart&userID=${userID}&recipientEmail=${email}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
      },
    }
  );
  const data = await response.json();

  if (data.status === 200) {
    return data.result;
  }
}

export async function logout() {
  /**
   * Logs out the user and cleans up the session.
   * @returns {Promise<Object>} - The object containing the result of the logout operation.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */
  let response = await fetch(link + "logout", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json; charset=UTF-8",
      Accept: "application/json, text/plain, */*",
    },
  });

  const data = await response.json();
  if (data.status === 200) {
    return data.result;
  }
}

export async function refreshSession() {
  /**
   * Refreshes the user session.
   *
   * @returns {Promise<Object>} The result of the request. If the request is
   * successful, the result contains the user data. Otherwise, the result is
   * an empty object.
   * @throws {Error} If the request fails.
   * @author Lionel Ghogue <lionelladrog@gmail.com>
   */

  const response = await fetch(link + `refreshSession`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });
  const data = await response.json();

  if (data.status === 200) {
    return data.result;
  }
}

export async function checkSession(userID) {
  const { store } = require("../store/store");
  const { setSession } = require("../store/reducer");

  const response = await fetch(link + `checkSession&userID=${userID}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "*/*",
    },
  });
  const data = await response.json();

  if (data.status === 200) {
    // console.log('data:',store.dispatch(setSession(true)));
    // store.dispatch(setSession(data.result));
    console.log("data:", data);
    return data;
  }
}
